export enum AdImageFormats {
  SQUARE = "square", // 1:1
  PORTRAIT_FEED = "portrait_feed", // 4:5
  LANDSCAPE = "landscape", // 16:9
  VERTICAL_FULLSCREEN = "vertical_fullscreen", // 9:16
  LANDSCAPE_CLASSIC = "landscape_classic", // 3:2
  PORTRAIT_POSTER = "portrait_poster", // 2:3
}
export interface IAdImage {
  key: string;
  width: number;
  height: number;
  format: AdImageFormats;
  fullUrl?: string;
}

export enum AdVideoFormats {
  PREROLL = "preroll",
  MIDROLL = "midroll",
  POSTROLL = "postroll",
  REEL = "reel",
}

export interface IAdVideo {
  key: string;
  duration: number;
  width: number;
  height: number;
  format: AdVideoFormats;
  skipAfterSec?: number;
  fullUrl?: string;
}

export interface ICreateAdCampaign {
  // --- core ---
  type?: "display" | "video";
  businessName?: string;
  headline?: string; // Ad headline
  description?: string;
  finalURL?: string;

  // --- creatives ---
  images?: IAdImage[];
  logos?: string[];
  videos?: IAdVideo[];

  // --- bidding & budgets ---
  bidding?: string;
  totalBudget?: number; // Total budget in chosen currency
  bidAmount?: number; // Cost per click/view/impression
  dailyBudget?: number; // Daily budget in chosen currency

  // --- targeting ---
  locations?: string[]; // e.g., ["IN", "US-CA"]
  languages?: string[]; // e.g., ["en","hi"]

  // --- lifecycle ---
  startDate?: string;
  endDate?: string;
  step?: string;
}

export interface IUpdateAdCampaign extends ICreateAdCampaign {
  // --- bidding & budgets ---
  pacing?: "even" | "asap";

  // --- targeting ---
  sites?: string[]; // domains/bundles
  slots?: string[]; // "300x250","320x50","preroll"
  devices?: string[]; // "web","android","ios","ott"
  kv?: { [key: string]: string[] }; // { genre: ["music"], plan:["free"] }

  // --- lifecycle ---
  status?:
  | "active"
  | "paused"
  | "completed"
  | "inreview"
  | "suspended"
  | "readyToSubmit"
  | "draft";
}

export interface IActionOnCampaign {
  id: string[];
  action:
  | "activate"
  | "pause"
  | "complete"
  | "inreview"
  | "suspend"
  | "draft"
  | "readyToSubmit";
}

export interface IGetCampaignsQuery {
  page?: number;
  limit?: number;
  order?: "asc" | "desc";
  sortBy?: string;
  id?: string;
  q?: string;
  slug?: string;
  select?: string;
  filters?: object;
}

export interface IAdCampaign {
  // --- core ---
  _id: string;
  type: "display" | "video";
  businessName: string; // Advertiser/business name
  headline: string; // Ad headline
  description: string; // Ad description
  finalURL: string; // Landing page after click

  // --- creatives ---
  images: IAdImage[]; // Ad creative images
  logos: string[]; // Brand logos
  videos: IAdVideo[]; // Video creatives

  // --- bidding & budgets ---
  bidding: string; // "CPC", "CPV", "CPM", etc.
  bidAmountCents: number; // Cost per click/view/impression
  dailyBudgetCents: number; // Daily budget in chosen currency
  totalBudgetCents: number; // Total budget in chosen currency
  spentTodayCents: number; // Spent today in chosen currency
  spentTotalCents: number; // Spent total in chosen currency
  pacing: "even" | "asap"; // even means even distribution across days or asap means as soon as possible

  // --- targeting ---
  locations: string[]; // e.g., ["IN", "US-CA"]
  languages: string[]; // e.g., ["en","hi"]
  sites: string[]; // domains/bundles
  slots: string[]; // "300x250","320x50","preroll"
  devices: string[]; // "web","android","ios","ott"
  kv: { [key: string]: string[] }; // { genre: ["music"], plan:["free"] }

  // --- lifecycle ---
  startDate: string | Date;
  endDate: string | Date;
  status:
  | "active"
  | "paused"
  | "completed"
  | "inreview"
  | "suspended"
  | "draft"
  | "readyToSubmit";
  step: string;

  // --- metrics (aggregated) ---
  clicks: number; // Total clicks received
  impressions: number; // Total times ad shown
  skips: number; // Total times ad skipped
  ctr: number; // Click-through rate (clicks / impressions) * 100
  totalSpentCents: number; // Total amount spent

  // --- caps ---
  freqCapN: number; // how many times
  freqCapWindowSec: number; // time window in seconds

  // Audit & meta
  createdAt: string;
  updatedAt: string;
}

export interface IGetAdsQuery {
  type?: "display" | "video";
  duration?: number;
  limit?: number;
}

export interface IServeAd {
  adType: "video" | "display";
  position: number | undefined;
  skippableAfter: number | undefined;
  mediaUrl: string;
  clickUrl: string;
  overlay: {
    logo: string;
    headline: string;
    description: string;
  };
  tracking: {
    impression: string;
    click: string;
    skip: string;
  };
}

export interface IGenerateAdQuery {
  url: string;
}

export interface IAutoGenerate {
  metadata: {
    title: string;
    description: string;
    url: string;
    favicon: string | null;
  };
  ads: {
    headlines: string[];
    descriptions: string[];
  };
}

export interface IAdTrackQuery {
  event: "impression" | "click" | "skip" | string;
  token: string; // JWT tracking token from ad response
}

export interface IRetrieveAdsQuery {
  type?: "display" | "video";
  duration?: number;
  limit?: number;
  slot?: string;
  device?: string;
  output?: "json" | "vast" | "vmap" | "vpaid" | "custom_vast_url";
  xSid?: string;
}
